import {
  GEVINSTVURDERINGSNAVN,
  belopFraDesimaltekst,
  belopFraTall,
  konverterBelop,
  belopTilDesimaltekst,
  tallFraCelle,
  type Gevinstvurdering,
  type MeldGevinst,
  type VedtattKutt,
} from '@es/shared';
import { manedsnavn } from '../../felles/format.js';

/**
 * Vedtatte kutt, slik siden sier det: vurderingene i ord, og beløpene lest
 * fra feltene. Se `domain/gevinstplan.ts` for regnestykket.
 */

export const VURDERINGSNAVN = GEVINSTVURDERINGSNAVN;

/** Fargen på merket. Etter plan er et varsel; svikter og stanset er et merforbruk. */
export const VURDERINGSFARGE: Record<Gevinstvurdering, string> = {
  i_rute: 'bg-positiv-svak text-positiv',
  etter_plan: 'bg-varsel-svak text-varsel',
  svikter: 'bg-negativ-svak text-negativ',
  stanset: 'bg-negativ-svak text-negativ',
};

const REKKEFOLGE: readonly Gevinstvurdering[] = ['svikter', 'stanset', 'etter_plan', 'i_rute'];

/** «1 svikter · 2 i rute», det alvorligste først. Tomt når det ikke er noen kutt. */
export function antallTekst(antall: Readonly<Record<Gevinstvurdering, number>>): string {
  return REKKEFOLGE.filter((v) => antall[v] > 0)
    .map((v) => `${String(antall[v])} ${VURDERINGSNAVN[v].toLowerCase()}`)
    .join(' · ');
}

/** Hva «planlagt hittil» er regnet til. */
export function plantekst(tilOgMed: number | null): string {
  return tilOgMed === null
    ? 'Regnskapet for året er ikke ført ennå, så ingenting er planlagt realisert.'
    : `Planlagt hittil er det vedtatte fordelt over månedene kuttet virker, til og med ${manedsnavn(tilOgMed)}.`;
}

/**
 * Et beløp i kroner fra et felt: «1 200 000», «250000,5». Null for et tomt
 * felt; undefined når feltet ikke er et tall.
 */
export function lesKroner(tekst: string): string | null | undefined {
  const tall = tallFraCelle(tekst);
  if (tall === null || tall === undefined) return tall;
  return belopTilDesimaltekst(belopFraTall(tall));
}

/** Et beløp til et felt: hele kroner, uten tusenskille. */
export function kronerTilFelt(desimaltekst: string | null): string {
  if (desimaltekst === null) return '';
  return desimaltekst.replace(/\.?0+$/, '').replace('.', ',');
}

export interface Meldefelt {
  readonly status: VedtattKutt['status'];
  readonly fremdriftProsent: string;
  readonly realisert: string;
  readonly forventet: string;
}

export function tilMeldefelt(k: VedtattKutt): Meldefelt {
  return {
    status: k.status,
    fremdriftProsent: String(k.fremdriftProsent),
    realisert: kronerTilFelt(k.realisert === '0.000' ? null : k.realisert),
    forventet: kronerTilFelt(k.forventet),
  };
}

/** Feltene til det som sendes, eller en feilmelding for det første som ikke er riktig. */
export function lesMelding(f: Meldefelt): MeldGevinst | string {
  const fremdrift = Number(f.fremdriftProsent.trim() === '' ? '0' : f.fremdriftProsent);
  if (!Number.isInteger(fremdrift) || fremdrift < 0 || fremdrift > 100) {
    return 'Fremdriften er et helt tall fra 0 til 100.';
  }
  const realisert = lesKroner(f.realisert);
  if (realisert === undefined) return 'Realisert gevinst må være et beløp i kroner.';
  const forventet = lesKroner(f.forventet);
  if (forventet === undefined) return 'Forventet for året må være et beløp i kroner.';
  return {
    status: f.status,
    fremdriftProsent: fremdrift,
    realisertGevinst: realisert,
    forventetGevinst: forventet,
  };
}

/** Kroner til tusen kroner, for rapporten der alle beløp står i tusen. */
export function iTusen(kroner: string): string {
  return belopTilDesimaltekst(konverterBelop(belopFraDesimaltekst(kroner), 'kr', 'kr_1000'));
}
